import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import Card from '@/components/ui/Card'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

const SPORT_PRESETS = [
  'basketball', 'football', 'track', 'wrestling', 'soccer', 'baseball', 'softball',
  'volleyball', 'tennis', 'swimming', 'golf', 'lacrosse', 'hockey', 'cheer', 'dance',
]

interface ProspectData {
  id: number
  name: string | null
  avatar_url: string | null
  headline: string | null
  bio: string | null
  athletic_profile: {
    sports?: string[]
    position?: string
    height_in?: number | null
    weight_lbs?: number | null
    class_year?: number | null
    age_group?: string
    team?: string
    location?: string
  } | null
}

function formatHeight(inches?: number | null) {
  if (!inches) return ''
  return `${Math.floor(inches / 12)}'${inches % 12}"`
}

export default function ProspectProfile() {
  const { session } = useAuth()
  const token = session?.access_token || ''

  const [prospect, setProspect] = useState<ProspectData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  // Form state
  const [name, setName] = useState('')
  const [headline, setHeadline] = useState('')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [sports, setSports] = useState<string[]>([])
  const [sportInput, setSportInput] = useState('')
  const [position, setPosition] = useState('')
  const [heightIn, setHeightIn] = useState<number | null>(null)
  const [weightLbs, setWeightLbs] = useState<number | null>(null)
  const [classYear, setClassYear] = useState<number | null>(null)
  const [ageGroup, setAgeGroup] = useState('')
  const [team, setTeam] = useState('')
  const [location, setLocation] = useState('')

  const loadProfile = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${CORE_API}/api/v1/roster/my-profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load profile')
      const json = await res.json()
      const p = json.data?.prospect as ProspectData
      if (p) {
        setProspect(p)
        setName(p.name || '')
        setHeadline(p.headline || '')
        setBio(p.bio || '')
        setAvatarUrl(p.avatar_url || '')
        const ap = p.athletic_profile || {}
        setSports(ap.sports || [])
        setPosition(ap.position || '')
        setHeightIn(ap.height_in ?? null)
        setWeightLbs(ap.weight_lbs ?? null)
        setClassYear(ap.class_year ?? null)
        setAgeGroup(ap.age_group || '')
        setTeam(ap.team || '')
        setLocation(ap.location || '')
      }
    } catch (err: any) {
      setError(err?.message || 'Unable to load')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { if (token) loadProfile() }, [loadProfile, token])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setSaved(false)
    setError('')
    try {
      const res = await fetch(`${CORE_API}/api/v1/roster/my-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || null,
          headline: headline.trim() || null,
          bio: bio.trim() || null,
          avatar_url: avatarUrl.trim() || null,
          athletic_profile: {
            sports,
            position: position.trim() || undefined,
            height_in: heightIn,
            weight_lbs: weightLbs,
            class_year: classYear,
            age_group: ageGroup.trim() || undefined,
            team: team.trim() || undefined,
            location: location.trim() || undefined,
          },
        }),
      })
      if (!res.ok) throw new Error('Failed to save')
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err: any) {
      setError(err?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }, [name, headline, bio, avatarUrl, sports, position, heightIn, weightLbs, classYear, ageGroup, team, location, token])

  const addSport = (s: string) => {
    const val = s.trim().toLowerCase()
    if (val && !sports.includes(val)) setSports([...sports, val])
    setSportInput('')
  }

  if (loading) return <div className="py-8 text-center text-sm text-stone-500">Loading profile...</div>

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-stone-900">My Profile</h2>
        <p className="text-sm text-stone-600">This is how you appear on the roster, leaderboards, and to your coach.</p>
      </div>

      {error && <p className="text-sm text-brick">{error}</p>}

      <Card className="space-y-5">
        {/* Name + Avatar */}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm font-medium text-stone-700">
            Display Name
            <input className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
          </label>
          <label className="block text-sm font-medium text-stone-700">
            Avatar URL
            <input className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none" value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder="https://..." />
          </label>
        </div>

        {/* Headline + Bio */}
        <label className="block text-sm font-medium text-stone-700">
          Headline
          <input className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Point Guard · Class of 2027" />
        </label>
        <label className="block text-sm font-medium text-stone-700">
          Bio
          <textarea className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell your coach and teammates about yourself" />
        </label>
      </Card>

      {/* Athletic Profile */}
      <Card className="space-y-5">
        <h3 className="text-sm font-bold uppercase tracking-wider text-stone-400">Athletic Profile</h3>

        {/* Sports */}
        <div>
          <p className="text-sm font-medium text-stone-700 mb-1">Sports</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {sports.map((s) => (
              <span key={s} className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-800">
                {s}
                <button type="button" onClick={() => setSports(sports.filter((x) => x !== s))} className="text-indigo-400 hover:text-indigo-600">&times;</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input className="flex-1 rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={sportInput} onChange={(e) => setSportInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSport(sportInput) } }} placeholder="Add sport..." list="sport-list" />
            <button type="button" onClick={() => addSport(sportInput)} className="rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white">Add</button>
          </div>
          <datalist id="sport-list">{SPORT_PRESETS.map((s) => <option key={s} value={s} />)}</datalist>
        </div>

        {/* Core measurables */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <label className="text-sm text-stone-700">
            <span className="text-xs font-medium">Position</span>
            <input className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="PG" />
          </label>
          <label className="text-sm text-stone-700">
            <span className="text-xs font-medium">Class Year</span>
            <input type="number" className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={classYear || ''} onChange={(e) => setClassYear(parseInt(e.target.value) || null)} placeholder="2027" />
          </label>
          <label className="text-sm text-stone-700">
            <span className="text-xs font-medium">Height (in)</span>
            <input type="number" className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={heightIn || ''} onChange={(e) => setHeightIn(parseInt(e.target.value) || null)} placeholder="72" />
            {heightIn ? <span className="text-[10px] text-stone-400">{formatHeight(heightIn)}</span> : null}
          </label>
          <label className="text-sm text-stone-700">
            <span className="text-xs font-medium">Weight (lbs)</span>
            <input type="number" className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={weightLbs || ''} onChange={(e) => setWeightLbs(parseInt(e.target.value) || null)} placeholder="180" />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <label className="text-sm text-stone-700">
            <span className="text-xs font-medium">Team</span>
            <input className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={team} onChange={(e) => setTeam(e.target.value)} placeholder="Varsity" />
          </label>
          <label className="text-sm text-stone-700">
            <span className="text-xs font-medium">Age Group</span>
            <input className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} placeholder="U16" />
          </label>
          <label className="text-sm text-stone-700">
            <span className="text-xs font-medium">Location</span>
            <input className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Houston, TX" />
          </label>
        </div>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-4">
        <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-stone-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
        {saved && <span className="text-sm font-medium text-emerald-600">Saved! Changes are live on the roster.</span>}
      </div>
    </section>
  )
}
